import React, { FunctionComponent } from 'react';
import {
  Chip,
  ChipProps,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';

import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import {
  ISubscriptionPlan,
  SUBSCRIPTION_PLAN_STATUSES,
} from '../../../../../../store/V2/SubscriptionPlansSlice';
import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../helper';
import { ICourse } from '../../../../../../store/V2/CoursesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../../../../store/V2/TenantsSlice';

interface SubscriptionPlanTableRowProps {
  subscriptionPlan: ISubscriptionPlan;
  onUpdate: () => void;
  onDelete: () => void;
  onView: () => void;
  onDuplicate: () => void;
  isActionsDisabled?: boolean;
}

const SubscriptionPlanTableRow: FunctionComponent<SubscriptionPlanTableRowProps> = ({
  subscriptionPlan,
  onUpdate,
  onDelete,
  onView,
  onDuplicate,
  isActionsDisabled = false,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  let coursesText = '';

  const courses = (subscriptionPlan.courses || []) as ICourse[];
  const statusBadgeProps: ChipProps = {
    size: 'small',
    label: subscriptionPlan.status,
    color: 'success',
    sx: {
      textTransform: 'capitalize',
      width: '100%',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 600,
    },
  };

  const createPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_CREATE,
      ],
    },
  });

  const updatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_EDIT,
      ],
    },
  });

  const deletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SUBSCRIPTION_PLANS_DELETE,
      ],
    },
  });

  if (subscriptionPlan.status === SUBSCRIPTION_PLAN_STATUSES.INACTIVE) {
    statusBadgeProps.color = 'error';
  }

  if (courses.length) {
    courses.forEach((item) => {
      coursesText += `, ${item.title}`;
    });

    coursesText = coursesText.substring(1).trim();
  } else {
    coursesText = '-';
  }

  return (
    <StyledTableRow hover key={subscriptionPlan._id}>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {subscriptionPlan.name}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {coursesText}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {subscriptionPlan.billingPeriod.count}{' '}
          {subscriptionPlan.billingPeriod.type.toLocaleLowerCase().slice(0, -1)}
          {subscriptionPlan.billingPeriod.count > 1 ? 's' : ''}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {subscriptionPlan.criteria
            ? subscriptionPlan.criteria.toLowerCase()
            : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Chip {...statusBadgeProps} />
      </StyledTableCell>
      <StyledTableCell>
        <Stack direction="row" alignItems="center">
          <Tooltip title="View">
            <IconButton disabled={isActionsDisabled} onClick={onView}>
              <VisibilityIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
          {updatePermission ? (
            <Tooltip title="Edit">
              <IconButton disabled={isActionsDisabled} onClick={onUpdate}>
                <EditIcon sx={{ color: '#1E293B' }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {createPermission ? (
            <Tooltip title="Duplicate">
              <IconButton disabled={isActionsDisabled} onClick={onDuplicate}>
                <FileCopyIcon sx={{ color: '#1E293B' }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {deletePermission ? (
            <Tooltip title="Delete">
              <IconButton disabled={isActionsDisabled} onClick={onDelete}>
                <DeleteIcon sx={{ color: '#1E293B' }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default SubscriptionPlanTableRow;
